/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext, useMemo } from 'react';

import Head from 'next/head';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { OurAwards } from 'ui/molecules/OurAwards/OurAwards';
import { CoverageSection } from 'ui/organisms/CoverageSection/CoverageSection';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';
import { DataFlowSection } from 'ui/organisms/DataFlowSection/DataFlowSection';
import { StepSwitcherSection } from 'ui/organisms/StepSwitcherSection/StepSwitcherSection';
import { TestimonialsSpotlightSection } from 'ui/organisms/TestimonialsSpotlightSection/TestimonialsSpotlightSection';

import { DifferenceSection } from 'features/home/pages/HomePage/sections/DifferenceSection/DifferenceSection';
import { HomeHeroSection } from 'features/home/pages/HomePage/sections/HomeHeroSection/HomeHeroSection';
import { HomeRegistriesSection } from 'features/home/pages/HomePage/sections/HomeRegistriesSection/HomeRegistriesSection';

import { getChannelsData, getCoverageRows, getHomeTestimonials, getUseCaseSteps } from './HomePage.data';

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const HomePage = () => {
  const { config, pageVars, secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const commonPageVars = useMemo(() => pageVars.common || {}, [pageVars.common]);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              url: 'https://www.globaldatabase.com/',
              name: 'GlobalDatabase',
              location: "Artisans' House, 7 Queensbridge, Northampton, Northamptonshire, NN4 7BF, United Kingdom",
              alternateName: 'Global Database',
              legalName: 'Global Data Intelligence Limited',
              description: config.schema_description,
              logo: config.schema_logo?.url,
              sameAs: [
                commonPageVars.linkedin,
                'https://www.quora.com/topic/Global-Database-1/',
                commonPageVars.facebook,
                commonPageVars.twitter,
                'https://www.crunchbase.com/organization/global-database-2',
                'https://www.youtube.com/c/GlobalDatabase',
                'https://www.instagram.com/global_database/',
              ].filter((i) => i),
              contactPoint: [
                {
                  '@type': 'ContactPoint',
                  telephone: commonPageVars.phone || '',
                  contactType: commonPageVars.customer_service || '',
                },
              ],
            }),
          }}
        />
      </Head>

      <div className="main-container">
        <HomeHeroSection freeTrialRoute={secondaryNavigation?.free_trial?.slug} />

        <StepSwitcherSection
          eyebrow={t('Use cases')}
          title={
            <>
              {t('One source of truth.')} <span>{t('Every company decision.')}</span>
            </>
          }
          description={t(
            "Whether you're onboarding a merchant, underwriting a loan, untangling an ownership chain or sizing a market — the answer starts at the registry.",
          )}
          steps={getUseCaseSteps(t)}
        />

        <HomeRegistriesSection title={t('Sourced directly from 400+ government registries.')} />

        <DifferenceSection />

        <CoverageSection
          title={t('See what we hold, country by country.')}
          description={t('Registries, filings, financials and ownership — documented for every jurisdiction we cover.')}
          actions={
            <Button type="blue" size="lg" route="/data-sources">
              {t('View our data sources')}
            </Button>
          }
          rows={getCoverageRows(t)}
        />

        <DataFlowSection
          title={t('How you get the data.')}
          sourceTitle={
            <>
              {t('One verified')}
              <br />
              {t('dataset.')}
              <br />
              <span>{t('600M+ companies.')}</span>
            </>
          }
          sourceText={t(
            'Sourced from 400+ government registries across 200+ countries. Every record attributed to the filing it came from.',
          )}
          channels={getChannelsData(t)}
        />

        <TestimonialsSpotlightSection
          title={t("What it's like to work with us")}
          ctaLabel={t('Read full case study')}
          ctaRoute={secondaryNavigation?.customers?.slug}
          items={getHomeTestimonials(t)}
        />

        <CtaSection
          theme="dark"
          glow
          title={t('Stop guessing. Go to the source.')}
          description={t(
            'See how primary, government-sourced data changes your compliance, risk and onboarding workflows.',
          )}
          actions={
            <>
              <Button
                type="white"
                size="xl"
                route={secondaryNavigation?.book_demo?.slug || '/book-a-demo'}
                suffix={<ArrowIcon />}
              >
                {t('Request a demo')}
              </Button>

              <Button type="white-outline" size="xl" route={secondaryNavigation?.pricing?.slug || '/pricing-products'}>
                {t('View our data')}
              </Button>
            </>
          }
        />

        <OurAwards
          title={t('Certified, audited, rated.')}
          description={t(
            'GDPR, CCPA and ISO 27001 compliant, hosted in the EU — and rated by the teams who use us every day.',
          )}
          variant="split"
        />
      </div>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default HomePage;
